import BaseBehavior from '../../../framework/base/base';
import { showElement, hideElement } from '../../../shared/utilities/show-hide';

export default class InterestRateCalculator extends BaseBehavior {
	constructor(element) {
		super(element);
		this.initDomCache();
		this.currencyItems = this.dom.currency.querySelector('.items');
		this.currentCurrency = {
			code: this.currencyItems.children[0].dataset.value,
			text: this.currencyItems.children[0].innerText
		};
		this.addEventListeners();
		const currency = this.dom.currencyValue.textContent.split(' ')[0];
		const nfeValue = this.dom.nfeSliderInput.value;
		this.dom.currencyValue.textContent = `${currency} ${this.returnCurrencyFormatObject().format(
			nfeValue
		)}`;
		this.iterationNumber = 0;
	}

	addEventListeners() {
		this.addEventListener(this.dom.calculateButton, 'click', this.getInterestTable.bind(this));
		this.addEventListener(this.dom.backTrigger, 'click', this.makeNewCalculation.bind(this));
		this.addEventListener(this.currencyItems, 'click', this.changeCurrency.bind(this));
		this.addEventListener(this.dom.reload, 'click', this.makeNewCalculation.bind(this));
		this.addEventListener(this.dom.nfeSliderInput, 'input', this.updateNfeLabel.bind(this));
	}

	/* Function to update label which has currency as well as amount, and update on change in 
	currency dropdown and slider input */
	updateNfeLabel(event) {
		const currency = this.currentCurrency.text;
		const val = event?.target?.value;
		const formatObj = this.returnCurrencyFormatObject();
		this.dom.currencyValue.textContent = `${currency} ${formatObj.format(val)}`;
	}

	returnCurrencyFormatObject() {
		const { culture } = this.element.dataset;
		return Intl.NumberFormat(culture);
	}

	changeCurrency(event) {
		const currencyCodeItem = event.target.closest('li');

		this.currentCurrency.text = currencyCodeItem.textContent;
		this.currentCurrency.code = currencyCodeItem.dataset.value;
		const nfeSliderData = JSON.parse(this.dom.nfeData.textContent).data;
		const selectedCurrencyNFEData = nfeSliderData.find(
			item => item.currency === this.currentCurrency.code
		);
		const sliderInput = this.dom.nfeSliderInput;

		if (selectedCurrencyNFEData) {
			sliderInput.setAttribute('value', selectedCurrencyNFEData.value);
			sliderInput.setAttribute('min', selectedCurrencyNFEData.min);
			sliderInput.setAttribute('max', selectedCurrencyNFEData.max);
			sliderInput.setAttribute('step', selectedCurrencyNFEData.step);
			sliderInput.value = selectedCurrencyNFEData.value;
		}
		// dispatching input event on slider to sync it with currency drodown changes(slider styles update)
		sliderInput.dispatchEvent(new Event('input'));
	}

	// Function to execute on click on make new calculation button.
	makeNewCalculation() {
		this.dom.tableSection.classList.remove('irc-responsive');
		showElement(this.dom.triggerSection);
		hideElement(this.dom.tableSection);
		hideElement(this.dom.errorSection);
		hideElement(this.dom.defaultView);
		hideElement(this.dom.tableView);
		hideElement(this.dom.nfeMax);
	}

	trackCalculation(data) {
		if (window.adobeDataLayer) {
			const amount = this.returnCurrencyFormatObject().format(data.nfeTotal);
			window.adobeDataLayer.push({
				event: 'interestRateCalculated',
				calculatorInfo: {
					selectedCurrency: data.currency,
					selectedAmount: amount,
					iterationNumber: ++this.iterationNumber
				}
			});
		}
	}

	getInterestTable(event) {
		event.preventDefault();
		const { culture, api } = this.element.dataset;
		const data = {
			culture,
			currency: this.currentCurrency.code,
			nfeTotal: this.dom.nfeSliderInput.value
		};
		this.trackCalculation(data);
		this.dom.inputDisplay.textContent = this.dom.currencyValue.textContent;
		hideElement(this.dom.triggerSection);
		hideElement(this.dom.errorSection);
		showElement(this.dom.loaderSection);
		return fetch(
			`${api}?culture=${data.culture}&currency=${data.currency}&nfeTotal=${data.nfeTotal}`
		)
			.then(response => {
				if (!response.ok) {
					throw new Error('There is some issue');
				}
				return response.json();
			})
			.then(result => {
				const dateString = result?.BidRateDate;
				const testDate = new Date(dateString);
				if (testDate.getTime() < 0) {
					throw new Error('There is some issue');
				}
				hideElement(this.dom.loaderSection);
				hideElement(this.dom.errorSection);
				showElement(this.dom.tableSection);
				if (result.HasTiers === false && result.Plans.length === 1) {
					if (result.Plans[0].IsMinNfe) {
						const minThresholdValue = result.Plans[0].MinRequiredNfe;
						const messageForMinValue = this.returnMinMessage(minThresholdValue, data.currency);
						this.renderMinView(messageForMinValue);
					} else {
						this.renderDefault(result);
					}
				} else if (result.IsMaxNfe) {
					showElement(this.dom.nfeMax);
					showElement(this.dom.defaultView);
				} else {
					this.renderInterestRates(result, data.currency);
				}
			})
			.catch(() => {
				hideElement(this.dom.loaderSection);
				showElement(this.dom.errorSection);
			});
	}

	// Function to return min message by accomodating currency and interest rate formatting changes.
	returnMinMessage(minThresholdValue, currencyValue) {
		const formatObj = this.returnCurrencyFormatObject();
		const minValueWithCurrency = `${currencyValue} ${formatObj.format(minThresholdValue)}`;
		const messageForMinValue = this.element.dataset.minValueMessage;
		return messageForMinValue.replace('%val%', minValueWithCurrency);
	}

	// Function to render dfefault view where we are showing interest rate and interest paid values.
	renderDefault(result) {
		let div = `<div class="v2-bg--white v2-pah15 v2-pat10 v2-pab20 v2-mab30 container">`;
		const tierData = result.Plans[0];
		const singlePlanDataKeys = JSON.parse(this.dom.singlePlan.textContent).data;
		singlePlanDataKeys.forEach(item => {
			let value;
			if (item.key === 'BlendedRate') {
				const formatObj = this.returnCurrencyFormatObject();
				value = formatObj.format(parseFloat(tierData[item.key]).toFixed(3)) + ' %';
			} else {
				value = `${this.currentCurrency.text} ${this.returnCurrencyFormatObject().format(
					tierData[item.key]
				)}`;
			}
			div += `<div class="v2-flexbox justify-between v2-pav10 row"><div>${item.name}</div><div>${value}</div></div>`;
		});

		div += `</div><p class="v2-p-s disclaimer">*Rates as of <span class="date">${this.getDisclaimerDate(
			result.BidRateDate
		)} </span> [disclaimer]</p>`;
		this.dom.defaultView.innerHTML = div;
		showElement(this.dom.defaultView);
	}

	getDisclaimerDate(disclaimerDate) {
		const date = new Date(disclaimerDate);
		const yyyy = date.getFullYear();
		let mm = date.getMonth() + 1; // Months start at 0!
		let dd = date.getDate();

		dd = dd < 10 ? '0' + dd : dd;
		mm = mm < 10 ? '0' + mm : mm;

		return dd + '-' + mm + '-' + yyyy;
	}

	// Function to show min or max view
	renderMinView(message) {
		const div = `<div class="v2-bg--white v2-pav30 v2-pah15 v2-mab30 container">${message}</div>`;
		this.dom.defaultView.innerHTML = div;
		showElement(this.dom.defaultView);
	}

	// Function to show tier table.
	renderInterestRates(result, currencyValue) {
		this.dom.tableSection.classList.remove('irc-responsive');
		showElement(this.dom.tableView);
		const tierData = result.Plans;
		const date = result?.BidRateDate;
		const disclaimer = this.element.querySelector('.disclaimer .date');
		disclaimer.textContent = this.getDisclaimerDate(date);
		const tbody = this.dom.tableSection.querySelector('tbody');
		let tableString = ``;
		tbody.innerHTML = '';

		const availableCols = Array.from(this.dom.tableView.querySelectorAll('th')).map(
			col => col.dataset.id
		);

		tierData.forEach(item => {
			tableString += '<tr>';
			const keys = Object.keys(item);
			availableCols.forEach(col => {
				if (keys.includes(col)) {
					if (item.IsMinNfe && col === 'BlendedRate') {
						const messageForMinValue = this.returnMinMessage(item.MinRequiredNfe, currencyValue);
						tableString += `<td>${messageForMinValue}</td>`;
					} else if (!item.IsMinNfe && col === 'BlendedRate') {
						const formatObj = this.returnCurrencyFormatObject();
						tableString += `<td>${formatObj.format(parseFloat(item[col]).toFixed(3))}%</td>`;
					} else if (col === 'InterestPaid') {
						tableString += item.IsMinNfe
							? `<td></td>`
							: `<td>${this.currentCurrency.text} 
								${this.returnCurrencyFormatObject().format(item[col])}</td>`;
					} else {
						tableString += `<td class="client-type">${item[col]}</td>`;
					}
				}
			});
			tableString += `</tr>`;
		});

		tbody.insertAdjacentHTML('beforeEnd', tableString);
	}

	cleanup() {
		this.removeEventListeners();
	}

	removeEventListeners() {
		this.removeEventListener(this.dom.calculateButton, 'click');
		this.removeEventListener(this.dom.backTrigger, 'click');
		this.removeEventListener(this.currencyItems, 'click');
		this.removeEventListener(this.dom.reload, 'click');
		this.removeEventListener(this.dom.nfeSliderInput, 'input');
	}
}
